import { Paper, Typography, Box, Button, Chip, IconButton, Tooltip } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { QueryGroup, QueryField } from '../../../../types/QueryBuilder';
import QueryRule from './QueryRule';

interface QueryRuleGroupProps {
    group: QueryGroup;
    categories: string[];
    fields: QueryField[];
    onAddRule: () => void;
    onUpdateRule: (ruleId: string, data: any) => void;
    onRemoveRule: (ruleId: string) => void;
    onRemoveGroup?: () => void;
    showCombinator?: boolean;
    canRemoveGroup?: boolean;
}

function QueryRuleGroup({
    group,
    categories,
    fields,
    onAddRule,
    onUpdateRule,
    onRemoveRule,
    onRemoveGroup,
    showCombinator = false,
    canRemoveGroup = false
}: QueryRuleGroupProps) {
    return (
        <Paper
            elevation={0}
            sx={{
                p: 2,
                mb: 2,
                bgcolor: '#f8f9fa',
                border: '1px solid #dee2e6',
                borderRadius: 2
            }}
        >
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography variant="subtitle2" sx={{ color: '#6c757d', fontWeight: 600 }}>
                        Groupe de règles
                    </Typography>
                    {showCombinator && (
                        <Chip
                            label={group.combinator}
                            size="small"
                            color="primary"
                            sx={{
                                fontWeight: 600,
                                bgcolor: '#556ee6',
                                color: 'white'
                            }}
                        />
                    )}
                </Box>
                
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Button
                        size="small"
                        startIcon={<AddIcon />}
                        onClick={onAddRule}
                        sx={{
                            textTransform: 'none',
                            color: '#556ee6',
                            '&:hover': {
                                backgroundColor: 'rgba(85, 110, 230, 0.08)'
                            }
                        }}
                    >
                        Ajouter une règle
                    </Button>
                    
                    {canRemoveGroup && onRemoveGroup && (
                        <Tooltip title="Supprimer ce groupe">
                            <IconButton
                                size="small"
                                onClick={onRemoveGroup}
                                sx={{
                                    color: '#dc3545',
                                    '&:hover': {
                                        backgroundColor: 'rgba(220, 53, 69, 0.1)'
                                    }
                                }}
                            >
                                <DeleteOutlineIcon fontSize="small" />
                            </IconButton>
                        </Tooltip>
                    )}
                </Box>
            </Box>

            {group.rules.length === 0 ? (
                <Box
                    sx={{
                        p: 3,
                        textAlign: 'center',
                        color: '#6c757d',
                        bgcolor: 'white',
                        borderRadius: 1,
                        border: '2px dashed #dee2e6'
                    }}
                >
                    <Typography variant="body2">
                        Aucune règle définie. Cliquez sur "Ajouter une règle" pour commencer.
                    </Typography>
                </Box>
            ) : (
                <>
                    {group.rules.map((rule, index) => (
                        <Box key={rule.id}>
                            <QueryRule
                                rule={rule}
                                categories={categories}
                                fields={fields}
                                onUpdateRule={onUpdateRule}
                                onRemoveRule={onRemoveRule}
                            />
                            {index < group.rules.length - 1 && (
                                <Box sx={{ textAlign: 'center', my: 1 }}>
                                    <Chip
                                        label="ET"
                                        size="small"
                                        sx={{
                                            bgcolor: '#28a745',
                                            color: 'white',
                                            fontWeight: 600,
                                            fontSize: '0.75rem'
                                        }}
                                    />
                                </Box>
                            )}
                        </Box>
                    ))}
                </>
            )}
        </Paper>
    );
};

export default QueryRuleGroup;
